/**
 * Slots deducidos para un template que nadie marcó.
 *
 * Lo correcto es marcar cada texto en el editor (`headline.1`,
 * `repeat.item.body`…). Mientras no se haga, un template sin marcas quedaba
 * fuera de la IA y un sitio salía con dos secciones. Aquí se deducen por la
 * forma: el primer título es el titular, el primer párrafo el subtítulo, los
 * botones las acciones, las imágenes las fotos y un grupo de tarjetas iguales
 * la lista repetible —con lo de cada tarjeta según la categoría: en
 * testimonios la primera línea es la cita, en cifras el número—.
 *
 * Sólo se usa si el template no tiene NINGUNA marca: en cuanto alguien marca
 * una, manda lo marcado. Trabaja sobre una copia.
 */
const HEADING_ROLES = new Set(['heading1', 'heading2', 'heading3', 'heading4', 'heading5', 'heading6']);
const HEADING_STYLES = /^h[1-6]$/;
const TEXT_TYPES = new Set(['text', 'richHeading']);
const ACTION_TYPES = new Set(['button', 'link']);
function hasText(node) {
    return typeof node.content === 'string' && node.content.replace(/<[^>]*>/g, '').trim().length > 0;
}
function textKind(node) {
    if (!TEXT_TYPES.has(node.type) || !hasText(node))
        return null;
    const typography = node.styleRefs?.typography;
    if (node.themeRole === 'eyebrow' || typography === 'small')
        return 'eyebrow';
    if (node.type === 'richHeading' || HEADING_ROLES.has(String(node.themeRole)) || (typeof typography === 'string' && HEADING_STYLES.test(typography)))
        return 'heading';
    return 'body';
}
/** La forma de un nodo —los tipos de dentro, en orden—, para reconocer tarjetas iguales. */
function signature(node, depth = 0) {
    if (depth > 4)
        return node.type;
    return `${node.type}(${(node.children ?? []).map((child) => signature(child, depth + 1)).join(',')})`;
}
function containsText(node) {
    return Boolean(textKind(node)) || (node.children ?? []).some(containsText);
}
/** Dos o más hijos contenedores con la misma forma y con texto: una lista de tarjetas. */
function isCardList(node) {
    const cards = (node.children ?? []).filter((child) => child.type === 'div');
    if (cards.length < 2 || cards.length !== (node.children ?? []).length)
        return false;
    const first = signature(cards[0]);
    return cards.every((card) => signature(card) === first) && containsText(cards[0]);
}
function walk(node, visit) {
    visit(node);
    (node.children ?? []).forEach((child) => walk(child, visit));
}
/** Qué es cada texto de una tarjeta, por categoría, en el orden en que aparecen. */
function itemTextSlots(category) {
    switch (category) {
        case 'testimonials':
            return (index) => ['repeat.item.body', 'repeat.item.author', 'person-role'][index] ?? null;
        case 'stats':
            return (index) => ['stat.value', 'stat.label'][index] ?? null;
        case 'team':
            return (index) => ['repeat.item.headline', 'person-role', 'repeat.item.body'][index] ?? null;
        case 'faq':
            return (index) => ['question', 'answer'][index] ?? null;
        case 'pricing': {
            let priced = false;
            return (index, text) => {
                if (index === 0)
                    return 'repeat.item.headline';
                if (!priced && /\d/.test(text)) {
                    priced = true;
                    return 'price.value';
                }
                return 'price.feature';
            };
        }
        default:
            return (index) => ['repeat.item.headline', 'repeat.item.body'][index] ?? null;
    }
}
function markItem(card, category) {
    card.aiSlot = 'repeat.item';
    const textSlot = itemTextSlots(category);
    let texts = 0;
    let images = 0;
    let actions = 0;
    let icons = 0;
    walk(card, (node) => {
        if (node === card)
            return;
        if (textKind(node)) {
            const slot = textSlot(texts, String(node.content));
            texts += 1;
            if (slot)
                node.aiSlot = slot;
        }
        else if (node.type === 'image' && images++ === 0)
            node.aiSlot = 'repeat.item.image';
        else if (node.type === 'icon' && icons++ === 0)
            node.aiSlot = 'repeat.item.icon';
        else if (ACTION_TYPES.has(node.type) && hasText(node) && actions++ === 0)
            node.aiSlot = 'repeat.item.action';
    });
}
function hasManualSlots(nodes) {
    return nodes.some((node) => (typeof node.aiSlot === 'string' && node.aiSlot.trim() !== '') || hasManualSlots(node.children ?? []));
}
/**
 * El template con slots deducidos, o tal cual si ya tenía alguno. `inferred`
 * dice cuántos se pusieron (0 = no hizo falta o no se encontró nada).
 */
export function inferTemplateSlots(elements, category) {
    if (hasManualSlots(elements))
        return { elements: [...elements], inferred: 0 };
    const copy = JSON.parse(JSON.stringify(elements));
    let headings = 0;
    let bodies = 0;
    let actions = 0;
    let images = 0;
    let eyebrow = false;
    let inferred = 0;
    const visit = (node) => {
        if (isCardList(node)) {
            node.aiSlot = 'repeat.list';
            node.children.forEach((card) => markItem(card, category));
            inferred += 1;
            return;
        }
        const kind = textKind(node);
        if (kind === 'eyebrow' && !eyebrow && headings === 0) {
            node.aiSlot = 'eyebrow';
            eyebrow = true;
            inferred += 1;
        }
        else if (kind === 'heading') {
            headings += 1;
            node.aiSlot = `headline.${headings}`;
            inferred += 1;
        }
        else if (kind === 'body' || (kind === 'eyebrow' && (eyebrow || headings > 0))) {
            bodies += 1;
            node.aiSlot = bodies === 1 ? 'subheadline.1' : `body.${bodies - 1}`;
            inferred += 1;
        }
        else if (ACTION_TYPES.has(node.type) && hasText(node) && actions < 2) {
            node.aiSlot = actions === 0 ? 'action.primary' : 'action.secondary';
            actions += 1;
            inferred += 1;
        }
        else if (node.type === 'image') {
            images += 1;
            node.aiSlot = `media.image.${images}`;
        }
        ;
        (node.children ?? []).forEach(visit);
    };
    copy.forEach(visit);
    return { elements: copy, inferred };
}
//# sourceMappingURL=templateSlotInference.js.map